/**
 * Planningsgrid — rijen (medewerkers) × kolommen (dagen) (Epic C-3 — Planbord
 * & indelen, Story C-3.3, UX-C1)
 *
 * `role="grid"`-tabel, zelfde structuurpatroon als `AanwezigheidGrid.tsx`
 * (sticky medewerker-kolom, `formatDateLabel`). Pijltjes-navigatie: elke
 * dagdeel-cel (`PlanningCell`'s interne gridcell-buttons) draagt
 * `data-row`/`data-col` zodat deze tabel een eenvoudige roving-focus kan
 * berekenen — géén volledige ARIA-grid-widget-implementatie, wel
 * functionele Arrow-navigatie tussen kolommen/rijen (AC 6).
 */

import { useRef } from 'react';
import type { BoardEmployeeEntry, PlanningTaskDTO } from '@functieloonhuis/shared';
import { PlanningCell } from './PlanningCell';
import { formatDateLabel } from '../../werkrooster/utils/dateUtils';

interface PlanningGridProps {
  employees: BoardEmployeeEntry[];
  dates: string[];
  todayIso: string;
  tasks: PlanningTaskDTO[];
  canManageTasks: boolean;
  /** Story C-4.3 (AC 2) — `${employeeId}-${date}` van de cel die kort gemarkeerd moet worden na "Aanpassen in planbord". */
  highlightedCellKey?: string | null;
}

export function PlanningGrid({ employees, dates, todayIso, tasks, canManageTasks, highlightedCellKey = null }: PlanningGridProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  if (employees.length === 0) {
    return (
      <div data-testid="planbord-leeg-team" className="text-center py-8 text-sm text-gray-500">
        Geen teamleden gevonden voor deze afdeling.
      </div>
    );
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    const target = e.target as HTMLElement;
    if (target.getAttribute('role') !== 'gridcell') return;

    const row = Number(target.getAttribute('data-row'));
    const col = Number(target.getAttribute('data-col'));
    if (Number.isNaN(row) || Number.isNaN(col)) return;

    let nextRow = row;
    let nextCol = col;
    if (e.key === 'ArrowRight') nextCol = col + 1;
    else if (e.key === 'ArrowLeft') nextCol = col - 1;
    else if (e.key === 'ArrowDown') nextRow = row + 1;
    else if (e.key === 'ArrowUp') nextRow = row - 1;
    else return;

    e.preventDefault();
    const next = containerRef.current?.querySelector<HTMLElement>(`[data-row="${nextRow}"][data-col="${nextCol}"]`);
    next?.focus();
  }

  return (
    <div ref={containerRef} className="overflow-x-auto" onKeyDown={handleKeyDown}>
      <table role="grid" data-testid="planning-grid" className="w-full border-collapse text-sm">
        <thead>
          <tr>
            <th className="text-left px-2 py-2 text-xs font-medium text-gray-500 sticky left-0 bg-white">Medewerker</th>
            {dates.map((date) => (
              <th key={date} className="px-1 py-2 text-[10px] font-medium text-gray-500 text-center min-w-[90px]">
                {formatDateLabel(new Date(`${date}T00:00:00`))}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {employees.map((employeeEntry, rowIndex) => (
            <tr key={employeeEntry.employee.id} className="border-t border-[#E2E8F0]">
              <td className="px-2 py-1 text-xs font-medium text-[#1E293B] sticky left-0 bg-white whitespace-nowrap align-top">
                {employeeEntry.employee.name}
              </td>
              {dates.map((date, colIndex) => {
                const day = employeeEntry.days.find((d) => d.date === date);
                if (!day) return <td key={date} className="px-1 py-1" />;
                return (
                  <td key={date} className="px-1 py-1 relative">
                    <PlanningCell
                      employeeId={employeeEntry.employee.id}
                      employeeName={employeeEntry.employee.name}
                      isBhv={employeeEntry.employee.isBhv}
                      date={date}
                      dayLabel={formatDateLabel(new Date(`${date}T00:00:00`))}
                      holidayName={day.holidayName}
                      morning={day.morning}
                      afternoon={day.afternoon}
                      tasks={tasks}
                      unqualifiedTaskIds={employeeEntry.unqualifiedTaskIds}
                      isPast={date < todayIso}
                      canManageTasks={canManageTasks}
                      rowIndex={rowIndex}
                      colIndex={colIndex}
                      highlighted={highlightedCellKey === `${employeeEntry.employee.id}-${date}`}
                    />
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
